/*
 * stubbyz-merch.js
 * ================
 * Alpine root component for the Stubbyz merch PDP. Holds the shared state that
 * both the grid section and the customiser snippet bind to:
 *   - qty: { variantId: n }        quantity per variant (compact + bulk write here)
 *   - method / colours / repeat / placement   decoration spec
 *   - art: { front, back }         uploaded artwork (set by stubbyz-customiser.js)
 *
 * Underbase is NOT a user choice. It is derived state: screen print on any
 * non-white garment always gets an underbase; white (and its family) never do;
 * other methods never do. Exposed as a read-only getter so it can neither be
 * toggled in the UI nor restored wrong from localStorage. See `underbase` below.
 *
 * Pricing shown here is a DISPLAY ESTIMATE only. The worker re-reads variant
 * prices server-side and is the authority. Never trust the browser for price.
 *
 * Decoration rates mirror the worker's DECO_RATES so the estimate matches, but
 * if they ever drift the worker still wins at checkout.
 */

(function () {
  // ---------------------------------------------------------------------------
  // HEADWEAR DECORATION METHOD METADATA  (edit copy + images here)
  // ---------------------------------------------------------------------------
  // Display order of method tiles. Only methods a product actually prices are
  // shown; any not listed here still appear (appended) so nothing is hidden.
  var SZ_METHOD_ORDER = [
    "embroidery", "supacolour", "supadtf", "supaetch",
    "supaflex", "supasub", "sublimation", "patch_straw"
  ];

  // Per-method display copy + image slots. These are DEFAULTS / FALLBACKS: the
  // admin-editable `decoration_method` metaobject (emitted into the island as
  // methodContent) overrides any field it supplies, per method, at runtime. Keep
  // these filled so a method with no metaobject entry still renders a full tile.
  // `image` is the tile thumbnail; `gallery` is the modal image list. Nothing
  // here affects pricing.
  var SZ_METHOD_META = {
    embroidery: {
      label: "Embroidery", badge: "Most popular",
      blurb: "Raised stitched thread. Premium feel, hard-wearing.",
      tags: ["Textured finish", "Best on caps & beanies", "Up to 15 thread colours"],
      longtext: "High-density decorative stitching, digitised from your logo and " +
        "sewn directly into the fabric. The classic premium finish for headwear — " +
        "durable, tactile, and built to outlast the cap.",
      image: "", gallery: []
    },
    supacolour: {
      label: "Supacolour", badge: "Full colour",
      blurb: "Crisp, vibrant full-colour heat transfer.",
      tags: ["Unlimited colours", "Photographic detail", "Applies over seams"],
      longtext: "An innovative full-colour transfer with crisp, vibrant graphics. " +
        "Handles free-floating text and fine detail with no weeding, sits flat on " +
        "curved cap fronts, and survives 100+ washes without peeling or fading.",
      image: "", gallery: []
    },
    supadtf: {
      label: "SupaDTF", badge: "Versatile",
      blurb: "Full-colour transfer for detail on any fabric.",
      tags: ["Vibrant + fine detail", "Great on mixed fabrics", "Small runs"],
      longtext: "Direct-to-film transfer for vibrant colour and fine detail across " +
        "diverse materials. Supports full-colour prints, gradients and photographic " +
        "images — a favoured choice for smaller production runs.",
      image: "", gallery: []
    },
    supaetch: {
      label: "SupaEtch", badge: "Premium patch",
      blurb: "Laser-etched suede patch, sewn on.",
      tags: ["High perceived value", "Subtle branding", "11 suede colours"],
      longtext: "A laser-etched suede patch sewn directly onto the product. A subtle, " +
        "premium branding option with high perceived value, available in 11 suede colours.",
      image: "", gallery: []
    },
    supaflex: {
      label: "SupaFlex", badge: "Detailed vinyl",
      blurb: "Detailed vinyl logo, matte or gloss.",
      tags: ["Any shape or detail", "Matte or gloss", "8 background colours"],
      longtext: "Highly detailed vinyl logos with a matte or gloss finish, attached " +
        "with embroidery for a quality result. Any shape, any design, any detail.",
      image: "", gallery: []
    },
    supasub: {
      label: "SupaSub", badge: "Hard-to-print",
      blurb: "Full-colour on hard-to-decorate items.",
      tags: ["Lightweight vs embroidery", "Choose thread colour", "High resolution"],
      longtext: "Replicates full-colour logos on hard-to-decorate products such as " +
        "caps, beanies, scarves and blankets. Lightweight compared to solid " +
        "embroidery, with your choice of thread colour for match or contrast.",
      image: "", gallery: []
    },
    sublimation: {
      label: "Sublimation", badge: "Dyed-in",
      blurb: "Ink dyed into the fabric itself.",
      tags: ["Becomes part of the fabric", "Vivid, crisp", "Low MOQ"],
      longtext: "A heat process that dyes synthetic fabric with sublimation inks, so " +
        "the decoration becomes part of the product rather than sitting on top — " +
        "vibrant colour with stunning detail.",
      image: "", gallery: []
    },
    patch_straw: {
      label: "Woven Patch", badge: "Specialty",
      blurb: "Applied patch for straw and specialty styles.",
      tags: ["Specialty styles", "Durable", "Branded label look"],
      longtext: "An applied woven or printed patch for straw hats and specialty " +
        "styles where direct decoration is not suitable.",
      image: "", gallery: []
    }
  };

  // Mirror of the worker DECO_RATES (display estimate only).
  var DECO_RATES = {
    "screen-printing": {
      impression: {
        1: { "25-49": 5.50, "50-99": 4.00, "100-249": 3.00, "250+": 2.50 },
        2: { "25-49": 7.50, "50-99": 5.50, "100-249": 4.00, "250+": 3.25 },
        4: { "25-49": 10.00, "50-99": 7.50, "100-249": 5.50, "250+": 4.50 }
      },
      setup: { new: 35.00, repeat: 17.50 }, setupPerUnit: "colour", moq: 25,
      qtyBrackets: ["25-49", "50-99", "100-249", "250+"]
    },
    "embroidery": {
      impression: { 1: { "10-19": 9.00, "20-34": 7.00, "35-49": 5.50, "50-99": 4.50, "100+": 3.75 } },
      setup: { new: 50.00, repeat: 25.00 }, setupPerUnit: "flat", moq: 10,
      qtyBrackets: ["10-19", "20-34", "35-49", "50-99", "100+"]
    },
    "dtg": { impression: { 1: { "any": 12.00 }, 2: { "any": 22.00 } }, setup: null, moq: 10, qtyBrackets: ["any"] },
    "dtf": { impression: { 1: { "any": 9.00 }, 2: { "any": 16.50 } }, setup: null, moq: 10, qtyBrackets: ["any"] }
  };

  var METHOD_LABELS = { "screen-printing": "Screen Print", "embroidery": "Embroidery", "dtg": "DTG", "dtf": "DTF" };

  // ---------------------------------------------------------------------------
  // GST DISPLAY CONFIG
  // Set this to match how prices are ENTERED in Shopify admin
  // (Settings > Taxes and duties > "All prices include tax").
  //   true  = variant prices are stored inc GST; we divide by 1.1 to show ex GST.
  //   false = variant prices are stored ex GST already; shown as-is.
  // AU stores default to tax-inclusive, so true is the likely setting. Confirm once.
  // ---------------------------------------------------------------------------
  var PRICES_INCLUDE_GST = true;
  var GST_RATE = 0.10;
  function exGst(n) { return PRICES_INCLUDE_GST ? (n / (1 + GST_RATE)) : n; }

  // Colours that count as "white" for underbase purposes. Non-white gets an
  // underbase on screen print. Keep this list in sync with the worker's copy.
  var WHITE_FAMILY = {
    "white": 1, "pfd white": 1, "sueded white": 1, "off white": 1, "offwhite": 1,
    "vintage white": 1, "prepared for dye": 1, "pfd": 1
  };
  function isWhiteColour(name) {
    return !!WHITE_FAMILY[String(name || "").toLowerCase().trim()];
  }

  function bracketFor(method, q) {
    var r = DECO_RATES[method]; if (!r) return null;
    for (var i = 0; i < r.qtyBrackets.length; i++) {
      var b = r.qtyBrackets[i];
      if (b === "any") return "any";
      var m = b.match(/^(\d+)-(\d+)$/);
      if (m && q >= +m[1] && q <= +m[2]) return b;
      var p = b.match(/^(\d+)\+$/);
      if (p && q >= +p[1]) return b;
    }
    return r.qtyBrackets[0];
  }

  function impressionRate(method, colours, q) {
    var r = DECO_RATES[method]; if (!r) return 0;
    var cb = 1;
    if (method === "screen-printing") cb = colours >= 4 ? 4 : (colours >= 2 ? 2 : 1);
    else if (method === "dtg" || method === "dtf") cb = colours >= 2 ? 2 : 1;
    var qb = bracketFor(method, q);
    var t = r.impression[cb];
    return t ? (t[qb] || 0) : 0;
  }

  function setupFee(method, colours, repeat, underbase) {
    var r = DECO_RATES[method]; if (!r || !r.setup) return 0;
    var base = repeat ? r.setup.repeat : r.setup.new;
    if (r.setupPerUnit === "colour") return base * ((colours || 1) + (underbase ? 1 : 0));
    return base;
  }

  function r2(n) { return Math.round(n * 100) / 100; }

  var HEX_MAP = {
    "black":"#111111","white":"#ffffff","navy":"#1f2a44","red":"#c8202f","royal":"#1e40af",
    "royal blue":"#1e40af","sport grey":"#9ca3af","sportgrey":"#9ca3af","grey":"#9ca3af","gray":"#9ca3af",
    "charcoal":"#3f3f46","ash":"#dcdcdc","ash grey":"#c9ccce","natural":"#efe9d8","cream":"#f3ead3",
    "maroon":"#5c1a1e","burgundy":"#5c1a2e","forest":"#1f3d2b","forest green":"#1f3d2b","military green":"#4b5320",
    "irish green":"#009a49","kelly green":"#0b6623","lime":"#9acd32","gold":"#e5b53b","daisy":"#f6d743",
    "orange":"#e5652c","safety orange":"#ff6a13","safety green":"#c6e51c","purple":"#5b2a86","light blue":"#add8e6",
    "sky":"#87ceeb","carolina blue":"#4b9cd3","indigo blue":"#3f4a8a","sapphire":"#1f4e9c","teal":"#127c78",
    "pink":"#f4a6c0","light pink":"#f6c9d9","heliconia":"#e14a8a","cardinal":"#8c1d2c","cardinal red":"#8c1d2c",
    "cherry red":"#b3122b","dark chocolate":"#3b2417","brown":"#5b3a29","sand":"#d8c9a3","bone":"#e3ddcc",
    "off white":"#f4f1e9","offwhite":"#f4f1e9","ice grey":"#d5d8da","graphite heather":"#54585c","dark heather":"#454b4f",
    "heather grey":"#b2b6b9","heather navy":"#2b3550","antique orange":"#c85a28","antique sapphire":"#1f6a8c",
    "antique irish green":"#3a7d44","antique jade dome":"#3c6e63","coral silk":"#f2795f","cornsilk":"#f5e6a8",
    "kiwi":"#8ea604","yellow haze":"#f0e39b","true navy":"#25314d","lieutenant":"#3b4a5a","celadon":"#b8ccb0",
    "powder blue":"#b0c4de","military":"#4b5320","asphalt":"#3a3f43","blush":"#e6b7bd"
  };
  var ASC_HEX_MAP = {
    "aqua":"#c0dfd8","arctic blue":"#2c78ac","arctic heather/navy":"#2c78ac","army":"#4e4a36",
    "army heather":"#4e4a36","army marle":"#4e4a36","army stone":"#4e4a36","army thatch":"#4e4a36",
    "army/black":"#4e4a36","army/cream":"#4e4a36","army/eucalyptus":"#4e4a36","army/natural":"#4e4a36",
    "army/white":"#4e4a36","ash heather":"#e4e4e6","ash stone":"#88878d","asphalt heather":"#50504b",
    "asphalt heather/black":"#50504b","asphalt marle":"#50504b","asphalt marle/black":"#50504b","asphalt thatch":"#50504b",
    "asphalt thatch/black":"#50504b","athletic fleck":"#a4a4a2","athletic heather":"#a4a4a2","athletic_h/asphalt_h":"#a4a4a2",
    "athletic_h/black":"#a4a4a2","athletic_h/burgundy":"#a4a4a2","atlantic":"#10535b","autumn":"#be4228",
    "avocado":"#b2b497","berry":"#8b4354","black":"#000000","black camo":"#000000",
    "black fleck":"#000000","black heather":"#302c2b","black marle":"#302c2b","black speckle":"#000000",
    "black stone":"#302c2b","black/athletic heather":"#000000","black/black":"#1e202c","black/burgundy":"#000000",
    "black/coal":"#000000","black/cream":"#000000","black/ecru":"#000000","black/grey marle":"#000000",
    "black/light grey":"#000000","black/natural":"#000000","black/red":"#000000","black/sand":"#000000",
    "black/white":"#000000","black/yellow":"#000000","blk/army/cypress":"#000000","blk/army/cyprs":"#000000",
    "blk/coal/musk":"#000000","blue stone":"#3c4259","blue white":"#5973a4","blue/black":"#1e202c",
    "blue/white":"#5973a4","bone":"#d1cdca","bone/black":"#ccbeb1","brick":"#773a39",
    "brick heather":"#773a39","brick marle":"#773a39","bright royal":"#395793","brown":"#5f3c2d",
    "bubblegum":"#eba3b1","burgundy":"#512024","burgundy heather":"#800020","burgundy/athletic_h":"#512024",
    "burgundy/grey marle":"#512024","butter":"#f4f0d7","camel":"#a67a3f","camel/black":"#a67a3f",
    "camo":"#4e4a36","camo/black":"#4e4a36","canary yellow":"#fbe342","candy pink":"#f1b3ca",
    "cardinal":"#821822","carolina blue":"#a0bfe2","charcoal":"#464646","charity pink":"#d56793",
    "charlotte":"#007a8e","chestnut":"#4e332a","citrus":"#bdea73","clay":"#875632",
    "clear":"#ffffff","coal":"#323031","coal/black":"#323031","cobalt":"#212b51",
    "cocoa":"#865e49","coffee":"#79634b","copper":"#88472d","copper/black":"#000000",
    "coral":"#9a5a53","cream":"#faf4e8","cream/black":"#faf4e8","cypress":"#51594a",
    "cypress/black":"#51594a","dark chocolate":"#3c2d26","dark grey":"#4c4c4c","dark grey/black":"#4c4c4c",
    "dark grey/burgundy":"#4c4c4c","dark navy":"#454959","denim":"#6c8ca5","denim blue":"#6184a4",
    "ecru":"#e4e0d4","ecru/black":"#e4e0d4","ecru/coal":"#e4e0d4","ecru/white":"#e4e0d4",
    "emerald":"#133f31","emerald/white":"#ffffff","essential 4 pack":"#000000","eucalyptus":"#938e71",
    "faded army":"#64644c","faded black":"#424243","faded blue":"#677083","faded bone":"#eae7e2",
    "faded brown":"#53463d","faded coal":"#3c3e40","faded dust":"#a5a196","faded ecru":"#f0eada",
    "faded eucalyptus":"#b7b6a4","faded grey":"#797470","faded indigo":"#535760","faded khaki":"#bdad93",
    "faded mauve":"#846f78","faded midnight":"#4a556b","faded mushroom":"#b2a795","faded mustard":"#c19d5f",
    "faded petrol":"#696e74","faded pink":"#bd9386","faded powder":"#bfc7d6","faded rose":"#bf8e8a",
    "faded slate":"#8da3a7","faded storm":"#a4aba7","faded taupe":"#aea393","faded teal":"#4c8074",
    "faded walnut":"#6d5d47","faded white":"#ffffff","faded wine":"#9e6969","fern green":"#6b823a",
    "fire":"#d2201c","fire/white":"#d2201c","folded catalog":"#f1fbfd","forest green":"#355138",
    "forest green/black":"#355138","forest heather":"#2c3f2b","forest marle":"#2c3f2b","forest speckle":"#21352a",
    "fuchsia":"#be2d56","gold":"#f8ac32","granite":"#a1a0a5","grape":"#692746",
    "graphite":"#3d3d3f","gravel":"#79797f","grey":"#a8a8a8","grey fleck":"#979694",
    "grey marle":"#a4a4a2","grey marle/asphalt marle":"#a4a4a2","grey marle/black":"#a4a4a2","grey marle/burgundy":"#a4a4a2",
    "grey marle/white":"#a4a4a2","grey speckle":"#a8a8a8","grey stone":"#4e4e4e","grey/ black":"#a8a8a8",
    "grey/black":"#a8a8a8","harbour blue":"#000000","hazy pink":"#bd9386","herringbone":"#ffffff",
    "hunter green heather":"#474c3f","hunter green marle":"#474c3f","hydro":"#3d81ae","ice white":"#f1fbfd",
    "indigo":"#29343a","ink blue":"#2d2a33","jade":"#084037","kelly green":"#027d4f",
    "khaki":"#ad8f69","khaki/black":"#ad8f69","kiwi":"#849161","lagoon":"#bdddda",
    "lapis":"#6772a8","lavender":"#d1b0c5","lemon":"#f3e58d","lemonade":"#eade8c",
    "liberty":"#253668","light blue":"#c2c9d9","light blue heather":"#adbacb","light blue marle":"#adbacb",
    "light green heather":"#6b823a","light green marle":"#6b823a","light grey":"#b9b5ac","light grey/black":"#b9b5ac",
    "light grey/midnight":"#b9b5ac","lime":"#dde0a7","lime/bone":"#dde0a7","marine blue":"#1c3438",
    "mauve":"#86727b","mid blue":"#3272af","mid blue/natural":"#3272af","midnight blue":"#35374c",
    "midnight navy heather":"#323642","midnight navy marle":"#323642","midnight/black":"#35374c","midnight/light grey":"#35374c",
    "midnight/natural":"#35374c","military green":"#5d6244","mineral":"#7e958d","mint":"#b7c2b2",
    "moss":"#8d8646","moss stone":"#676451","mushroom":"#b2a795","musk":"#8f796c",
    "mustard":"#e7ad52","natural":"#fbf7ee","natural marle/black":"#fbf7ee","natural walnut":"#fbf7ee",
    "natural/army":"#fbf7ee","natural/black":"#fbf7ee","natural/cardinal":"#fbf7ee","natural/coal":"#fbf7ee",
    "natural/forest":"#fbf7ee","natural/liberty":"#fbf7ee","natural/mid blue":"#fbf7ee","natural/midnight":"#fbf7ee",
    "natural/midnight blue":"#fbf7ee","natural/red":"#fbf7ee","natural/walnut":"#fbf7ee","natural_h/black":"#fbf7ee",
    "navy":"#1e202c","navy heather":"#383c46","navy marle":"#383c46","navy marle/white":"#1e202c",
    "navy speckle":"#242b35","navy thatch":"#373d46","navy/black":"#1e202c","navy/cream":"#1e202c",
    "navy/ecru":"#1e202c","navy/forest":"#1e202c","navy/red":"#1e202c","navy/white":"#1e202c",
    "oatmeal heather":"#ece9e0","oatmeal marle":"#ece9e0","ocean":"#4d91c6","ocean heather":"#566687",
    "ojay":"#f26022","olive":"#443727","orange":"#e56929","orchid":"#e3dde3",
    "orchid stone":"#b3a9b4","oyster":"#9b9e93","pale blue":"#b1c3c7","pale pink":"#f6d5c6",
    "peach":"#f4ba8a","petrol blue":"#404a53","pine green":"#1d342a","pine green/black":"#1d342a",
    "pink":"#f0d2d4","pistachio":"#b2b799","plum":"#433238","plywood":"#f8dfa1",
    "powder":"#989fb1","powder blue":"#a0aabf","powder/navy":"#989fb1","powder/white":"#989fb1",
    "purple":"#603e71","raw indigo":"#131822","red":"#bb1016","red/black":"#bb1016",
    "red/navy":"#bb1016","rose":"#d19b98","royal blue":"#395793","safari":"#817a65",
    "safety orange":"#ff6b07","safety pink":"#f535aa","safety yellow":"#eff162","sage":"#768d7d",
    "sand":"#b7b09d","seafoam":"#e6f3f4","sky blue":"#b7c7d7","slate blue":"#74929a",
    "smoke":"#98a0a5","smoke/ecru":"#98a0a5","smoke/white":"#98a0a5","steel":"#b2b2b2",
    "steel heather":"#868686","steel heather/black":"#868686","steel heather/navy":"#868686","steel marle":"#868686",
    "steel marle/black":"#868686","steel marle/navy":"#868686","steel marle/navy marle":"#868686","steel_h/navy_h":"#868686",
    "stone grey/black":"#929292","storm":"#a4aba7","sunset":"#f7c46c","tan":"#c6a379",
    "taupe":"#aa9f96","teal":"#0bb5b2","tobacco":"#805533","topaz":"#58ae95",
    "violet":"#543d7d","walnut":"#6b5a4a","walnut/black":"#6b5a4a","walnut/white":"#ffffff",
    "washed black":"#47484d","washed blue":"#667b98","watermelon":"#c84b61","white":"#ffffff",
    "white camo":"#dbd9da","white heather":"#e4e4e6","white marle":"#e4e4e6","white/black":"#ffffff",
    "white/charcoal":"#ffffff","white/coal":"#ffffff","white/green":"#ffffff","white/kelly green":"#ffffff",
    "white/navy":"#ffffff","white/pink":"#ffffff","white/red":"#ffffff","white/red/blue":"#ffffff",
    "white/rose":"#ffffff","white/sage":"#ffffff","white/yellow":"#ffffff","wood":"#f8dfa1",
    "yellow":"#e8c045","yellow/black":"#e8c045"
  };

  // isAsColour is set per-instance from the data island vendor. When true we
  // consult ASC_HEX_MAP first (AS Colour publishes official hex per colour, and
  // 28 names collide with Gildan at different values, so the maps must stay
  // separate to avoid shifting Gildan swatches).
  function hexForColour(name, ascFirst) {
    var k = String(name || "").toLowerCase().trim();
    if (ascFirst && ASC_HEX_MAP[k]) return ASC_HEX_MAP[k];
    if (HEX_MAP[k]) return HEX_MAP[k];
    // strip common prefixes for a looser match
    var k2 = k.replace(/^(heather|antique|faded|true|vintage)\s+/, "");
    if (ascFirst && ASC_HEX_MAP[k2]) return ASC_HEX_MAP[k2];
    if (HEX_MAP[k2]) return HEX_MAP[k2];
    return "#cccccc"; // neutral fallback so the swatch is never empty
  }

  window.stubbyzMerch = function () {
    return {
      // ---- data island ----
      data: null,
      variants: [],
      isAsColour: false,     // set from island vendor; drives ASC_HEX_MAP lookup
      colours: [],           // [{ name, hex }]
      sizeNames: [],
      // ---- decoration spec ----
      method: "screen-printing",
      colours_count: 1,       // alias below as `colours` getter conflict avoided
      repeat: false,
      // underbase is a derived getter (see below), never stored here.
      placement: "front",
      activeSide: "front",
      // ---- art (set by customiser JS) ----
      art: { front: null, back: null },
      // ---- quantity state ----
      qty: {},                // variantId -> n
      selectedColour: "",
      previewColour: "",
      bulkOpen: false,
      colourSearch: "",
      // ---- ui ----
      submitting: false,
      uploading: false,
      uploadError: "",
      errorMsg: "",
      dragging: false,
      merchWorker: "",
      uploadWorker: "",
      productId: null,
      printSides: "both",

      // ---- LL headwear path (populated only when custom.pricing is present) ----
      pricing: null,          // parsed custom.pricing object, or null for apparel
      decoZones: null,        // parsed custom.deco_zones object, or null
      methodContent: null,    // parsed methodContent (metaobject overrides), or null
      isHeadwear: false,      // true when pricing present -> headwear UI + math
      placements: [],         // [{ zone, method }] chosen by the customer (multi-zone)
      activeZone: "",         // zone id whose art-upload slot is currently active
      zonePickerMethod: "",   // method currently chosen in the add-placement UI
      zonePickerZone: "",     // zone key currently chosen in the add-placement UI

      init: function (el) {
        this.merchWorker  = (el.dataset.merchWorker  || "").replace(/\/$/, "");
        this.uploadWorker = (el.dataset.uploadWorker || "").replace(/\/$/, "");
        this.productId    = el.dataset.productId;
        this.printSides   = el.dataset.printSides || "both";

        var island = el.querySelector("[data-sz-variants]");
        if (island) {
          try { this.data = JSON.parse(island.textContent); } catch (e) { this.data = null; }
        }
        if (this.data) {
          this.variants = this.data.variants || [];
          this.isAsColour = /as\s*colour/i.test(this.data.vendor || "");
          // Headwear switch: presence of custom.pricing on the island.
          this.pricing   = this.data.pricing || null;
          this.decoZones = this.data.decoZones || null;
          this.methodContent = this.data.methodContent || null;  // metaobject overrides
          this.isHeadwear = !!(this.pricing && this.pricing.decoration &&
                               this.pricing.decoration.methods &&
                               Object.keys(this.pricing.decoration.methods).length);
          if (this.isHeadwear) { this.initHeadwear(); }
          this.buildColoursAndSizes();
          if (this.colours.length) { this.selectedColour = this.colours[0].name; this.previewColour = this.colours[0].name; }
        }
        // expose to the customiser JS so it can read/write art + placement
        window.__szMerch = this;

        // Restore saved design/quantities for this product (persist across reload).
        this.restoreState();
        // Re-place saved art on the overlay once it has booted.
        var self = this;
        setTimeout(function () { self.replaySavedArt(); }, 600);
      },

      // ================= LL HEADWEAR PATH =================
      // All of this is inert for apparel (isHeadwear === false).

      initHeadwear: function () {
        // Seed the add-placement picker with the first priced method + its first
        // zone, so the UI opens on a valid, selectable combination.
        var methods = this.headwearMethods();
        if (methods.length) {
          this.zonePickerMethod = methods[0].key;
          var zs = this.zonesForMethod(methods[0].key);
          if (zs.length) this.zonePickerZone = zs[0].id;
        }
      },

      // Tier resolver: the price whose `min` is the greatest <= qty. If qty is
      // below the smallest tier's min, we still return that smallest tier's price
      // (the entry rate) rather than nothing — every method is always priced.
      tierPrice: function (tiers, qty) {
        if (!Array.isArray(tiers) || !tiers.length) return null;
        var priced = tiers.filter(function (t) { return t && t.price != null; });
        if (!priced.length) return null;
        priced.sort(function (a, c) { return (a.min || 0) - (c.min || 0); });
        var best = priced[0];               // entry rate = smallest-min tier
        for (var i = 0; i < priced.length; i++) {
          if (qty >= (priced[i].min || 0)) best = priced[i];
        }
        return best.price;
      },

      // Methods offered for this product, in a stable display order, with labels.
      headwearMethods: function () {
        if (!this.isHeadwear) return [];
        var order = SZ_METHOD_ORDER;
        var m = this.pricing.decoration.methods || {};
        var out = [];
        for (var i = 0; i < order.length; i++) {
          var k = order[i];
          if (m[k]) out.push(this.methodMeta(k));
        }
        // any method present in pricing but not in the known order (future-proof)
        for (var k2 in m) { if (order.indexOf(k2) === -1) out.push(this.methodMeta(k2)); }
        return out;
      },

      // Full display metadata for one method: label, blurb, tag, sample image and
      // gallery for the info modal, plus the entry (from) price at MOQ.
      // Content precedence: the `decoration_method` metaobject (admin-editable)
      // overrides the built-in SZ_METHOD_META defaults, field by field, but only
      // where the metaobject actually supplies a value — so a half-filled entry
      // still falls back to sensible defaults and never blanks the tile.
      methodMeta: function (key) {
        var base = SZ_METHOD_META[key] || {};
        var mo   = (this.methodContent && this.methodContent[key]) || {};
        function pick(field, dflt) {
          var v = mo[field];
          if (v == null) return dflt;
          if (typeof v === "string" && v.trim() === "") return dflt;
          if (Array.isArray(v) && v.length === 0) return dflt;
          return v;
        }
        var tiers = ((this.pricing.decoration.methods[key] || {}).tiers) || [];
        var moq   = this.hwMoq();
        var from  = this.tierPrice(
          (key === "embroidery") ? this._collapseEmbTiers(tiers) : tiers,
          moq
        );
        return {
          key:      key,
          label:    pick("label",    base.label || key),
          badge:    pick("badge",    base.badge || ""),
          blurb:    pick("blurb",    base.blurb || ""),
          tags:     pick("tags",     base.tags  || []),
          image:    pick("image",    base.image || ""),
          gallery:  pick("gallery",  base.gallery || []),
          longtext: pick("longtext", base.longtext || base.blurb || ""),
          fromPrice: from
        };
      },

      // Helper mirroring decoUnitFor's embroidery band collapse, for from-pricing.
      _collapseEmbTiers: function (tiers) {
        if (!tiers.some(function (t) { return t && t.band; })) return tiers;
        var byMin = {};
        tiers.forEach(function (t) {
          if (!t || t.price == null) return;
          var k = t.min || 0;
          if (byMin[k] == null || t.price < byMin[k]) byMin[k] = t.price;
        });
        return Object.keys(byMin).map(function (k) { return { min: +k, price: byMin[k] }; });
      },

      // ---- method info modal + tile selection ----
      infoMethod: "",                                  // key of method shown in modal
      openInfo:  function (key) { this.infoMethod = key; },
      closeInfo: function () { this.infoMethod = ""; },
      infoView:  function () {
        return this.infoMethod ? this.methodMeta(this.infoMethod) : null;
      },
      // Tile click: select this method for the add-placement picker.
      pickMethod: function (key) {
        this.zonePickerMethod = key;
        this.onPickerMethodChange();
      },

      // Placement zones for a method, from custom.deco_zones. Each zone gets a
      // stable id and a display label that disambiguates same-named zones by size.
      zonesForMethod: function (methodKey) {
        if (!this.decoZones || !this.decoZones.methods) return [];
        var entry = this.decoZones.methods[methodKey];
        // SupaDTF and others may share another method's zones (shared_with).
        if (!entry && this.pricing.decoration.methods[methodKey]) {
          // fall back to supacolour zones if present (mirrors pricing inheritance)
          entry = this.decoZones.methods.supacolour || null;
        }
        if (!entry || !Array.isArray(entry.zones)) return [];
        var out = [];
        for (var i = 0; i < entry.zones.length; i++) {
          var z = entry.zones[i];
          var dims = (z.shape === "circle")
            ? ("Ø" + z.dia_mm + "mm")
            : (z.w_mm + " × " + z.h_mm + "mm");
          out.push({
            id: methodKey + ":" + i,
            name: z.name,
            label: z.name + " (" + dims + ")",
            shape: z.shape || "rect",
            w_mm: z.w_mm, h_mm: z.h_mm, dia_mm: z.dia_mm || null
          });
        }
        return out;
      },

      // A zone object by its composite id (methodKey:index).
      zoneById: function (zoneId) {
        if (!zoneId) return null;
        var mk = zoneId.split(":")[0];
        var zs = this.zonesForMethod(mk);
        for (var i = 0; i < zs.length; i++) if (zs[i].id === zoneId) return zs[i];
        return null;
      },

      // When the picker method changes, reset its zone to the first available.
      onPickerMethodChange: function () {
        var zs = this.zonesForMethod(this.zonePickerMethod);
        this.zonePickerZone = zs.length ? zs[0].id : "";
      },

      // Per-unit decoration cost for a method at the current total quantity.
      // Every method is flat-tiered by quantity. Embroidery, if it still carries
      // stitch-band tiers in the data, collapses to its cheapest priced band so
      // the customer always sees a real rate (no bands surfaced, no POA).
      decoUnitFor: function (methodKey) {
        var m = this.pricing.decoration.methods[methodKey];
        if (!m || !Array.isArray(m.tiers)) return null;
        var qty = this.totalQty();
        var tiers = m.tiers;
        if (methodKey === "embroidery" && tiers.some(function (t) { return t && t.band; })) {
          // Collapse bands: keep the cheapest priced tier at each min.
          var byMin = {};
          tiers.forEach(function (t) {
            if (!t || t.price == null) return;
            var k = t.min || 0;
            if (byMin[k] == null || t.price < byMin[k]) byMin[k] = t.price;
          });
          tiers = Object.keys(byMin).map(function (k) { return { min: +k, price: byMin[k] }; });
        }
        return this.tierPrice(tiers, qty);
      },

      // POA is retired: no method is ever priced-on-application.
      methodIsPOA: function () { return false; },

      // Add the currently-picked {zone, method} as a placement (no duplicates).
      addPlacement: function () {
        if (!this.zonePickerZone || !this.zonePickerMethod) return;
        var exists = this.placements.some(function (p) {
          return p.zone === this.zonePickerZone && p.method === this.zonePickerMethod;
        }, this);
        if (exists) return;
        this.placements.push({ zone: this.zonePickerZone, method: this.zonePickerMethod });
        this.activeZone = this.zonePickerZone;   // focus the new placement's art slot
        this.saveState();
      },

      setActiveZone: function (zoneId) { this.activeZone = zoneId; },

      removePlacement: function (idx) {
        var removed = this.placements[idx];
        this.placements.splice(idx, 1);
        if (removed && this.art[removed.zone]) { this.art[removed.zone] = null; this.art = Object.assign({}, this.art); }
        if (removed && this.activeZone === removed.zone) {
          this.activeZone = this.placements.length ? this.placements[0].zone : "";
        }
        this.saveState();
      },

      // A placement decorated with resolved label + unit cost, for display.
      placementView: function (p) {
        var z = this.zoneById(p.zone);
        var methods = this.headwearMethods();
        var label = (methods.find(function (m) { return m.key === p.method; }) || {}).label || p.method;
        var unit = this.decoUnitFor(p.method);
        return {
          methodLabel: label,
          zoneLabel: z ? z.label : p.zone,
          zoneName: z ? z.name : "",
          shape: z ? z.shape : "rect",
          unit: unit,
          poa: false
        };
      },

      // POA retired — nothing ever blocks Add-to-cart on price grounds.
      hasPOA: function () { return false; },

      // ---- headwear pricing ----
      // Garment (blank) sell price per unit at the current qty, from sell_tiers.
      hwBlankUnit: function () {
        var b = this.pricing.blank || {};
        var u = this.tierPrice(b.sell_tiers, this.totalQty());
        return (u == null) ? (b.sell_base || 0) : u;
      },

      // Total decoration cost per unit = sum of each placement's unit rate.
      hwDecoUnit: function () {
        var sum = 0;
        for (var i = 0; i < this.placements.length; i++) {
          var u = this.decoUnitFor(this.placements[i].method);
          if (u == null) continue;   // POA excluded from the running total
          sum += u;
        }
        return sum;
      },

      // Setup: one setup fee per placement (new setup). Repeat is 0 by data.
      hwSetupTotal: function () {
        var s = (this.pricing.decoration.setup || {});
        var per = (s.new != null) ? s.new : 0;
        return per * this.placements.length;
      },

      hwMoq: function () {
        return (this.pricing.decoration && this.pricing.decoration.moq) || 1;
      },

      // Per-unit ex-setup (blank + deco), and the all-in total incl. setup.
      hwUnitNow: function () { return this.hwBlankUnit() + this.hwDecoUnit(); },
      hwTotalNow: function () {
        return this.hwUnitNow() * this.totalQty() + this.hwSetupTotal();
      },

      // Headwear tier grid: one row per sell_tier break, showing the BLANK unit
      // price ex GST at that quantity (decoration shown separately per placement).
      hwTierRows: function () {
        var b = this.pricing.blank || {};
        var tiers = (b.sell_tiers || []).slice().sort(function (a, c) { return a.min - c.min; });
        if (!tiers.length) return [];
        var base = tiers[0].price;
        var total = this.totalQty();
        // active tier = greatest min <= total
        var activeMin = tiers[0].min;
        for (var i = 0; i < tiers.length; i++) if (total >= tiers[i].min) activeMin = tiers[i].min;
        var rows = [];
        for (var j = 0; j < tiers.length; j++) {
          var t = tiers[j];
          var label = (j < tiers.length - 1)
            ? (t.min + "-" + (tiers[j + 1].min - 1))
            : (t.min + "+");
          rows.push({
            label: label, min: t.min, unit: exGst(t.price),
            savePct: base > 0 ? Math.round((1 - t.price / base) * 100) : 0,
            isBase: j === 0, active: activeMin === t.min
          });
        }
        return rows;
      },

      // ---- underbase (DERIVED, read only) ----
      // Screen print on a non-white garment always gets an underbase. Everything
      // else never does. Follows the currently selected colour. Because this is a
      // getter with no setter, nothing in the UI or in restored state can flip it.
      get underbase() {
        if (this.method !== "screen-printing") return false;
        return !isWhiteColour(this.selectedColour);
      },

      storageKey: function () { return "szmerch:" + this.productId; },

      saveState: function () {
        try {
          var payload = {
            qty: this.qty,
            method: this.method,
            colours_count: this.colours_count,
            repeat: this.repeat,
            placement: this.placement,
            art: this.art,
            selectedColour: this.selectedColour,
            placements: this.placements,
            activeZone: this.activeZone
          };
          window.localStorage.setItem(this.storageKey(), JSON.stringify(payload));
        } catch (e) { /* storage unavailable, ignore */ }
      },

      restoreState: function () {
        try {
          var raw = window.localStorage.getItem(this.storageKey());
          if (!raw) return;
          var s = JSON.parse(raw);
          if (s.qty) this.qty = s.qty;
          if (s.method) this.method = s.method;
          if (s.colours_count) this.colours_count = s.colours_count;
          if (typeof s.repeat === "boolean") this.repeat = s.repeat;
          // underbase intentionally NOT restored, it is derived from method+colour.
          if (s.placement) this.placement = s.placement;
          if (s.art) this.art = s.art;
          if (s.selectedColour) { this.selectedColour = s.selectedColour; this.previewColour = s.selectedColour; }
          if (Array.isArray(s.placements)) this.placements = s.placements;
          if (s.activeZone) this.activeZone = s.activeZone;
        } catch (e) { /* ignore corrupt state */ }
      },

      replaySavedArt: function () {
        var self = this;
        ["front","back"].forEach(function (side) {
          var a = self.art && self.art[side];
          if (a && a.previewUrl) {
            document.dispatchEvent(new CustomEvent("artwork:uploaded", {
              detail: { side: side, url: a.previewUrl, savedPos: a.position || null }
            }));
          }
        });
      },

      clearSaved: function () {
        try { window.localStorage.removeItem(this.storageKey()); } catch (e) {}
      },

      // Alpine binds `colours` as the swatch list AND the colour-count in the
      // customiser. To avoid a name clash we expose the count under a getter.
      get coloursCount() { return this.colours_count; },
      setColours: function (n) { this.colours_count = n; this.saveState(); },

      buildColoursAndSizes: function () {
        var seenC = {}, seenS = {}, cList = [], sList = [];
        for (var i = 0; i < this.variants.length; i++) {
          var v = this.variants[i];
          var cname = v.colour || (v.options && v.options[this.data.colourOptionIndex]) || "";
          var sname = v.size || (v.options && v.options[this.data.sizeOptionIndex]) || "";
          if (cname && !seenC[cname]) { seenC[cname] = 1; cList.push({ name: cname, hex: v.hex || hexForColour(cname, this.isAsColour) }); }
          if (sname && !seenS[sname]) { seenS[sname] = 1; sList.push(sname); }
        }
        this.colours = cList;
        this.sizeNames = sList;
      },

      // ---- colour/size lookups ----
      variantFor: function (colour, size) {
        for (var i = 0; i < this.variants.length; i++) {
          var v = this.variants[i];
          var cn = v.colour || (v.options && v.options[this.data.colourOptionIndex]);
          var sn = v.size   || (v.options && v.options[this.data.sizeOptionIndex]);
          if (cn === colour && sn === size) return v;
        }
        return null;
      },
      sizesFor: function (colour) {
        var out = [];
        for (var i = 0; i < this.sizeNames.length; i++) {
          var v = this.variantFor(colour, this.sizeNames[i]);
          if (v) out.push({ id: v.id, size: this.sizeNames[i] });
        }
        return out;
      },
      cellVariant: function (colour, size) { return this.variantFor(colour, size); },
      cellQty: function (colour, size) { var v = this.variantFor(colour, size); return v ? (this.qty[v.id] || 0) : 0; },

      // ---- quantity mutations ----
      setQty: function (variantId, val) {
        var n = parseInt(val, 10); if (!(n > 0)) n = 0;
        this.qty[variantId] = n; this.qty = Object.assign({}, this.qty); this.saveState();
      },
      inc: function (variantId) { this.setQty(variantId, (this.qty[variantId] || 0) + 1); },
      dec: function (variantId) { this.setQty(variantId, (this.qty[variantId] || 0) - 1); },
      setCellQty: function (colour, size, val) { var v = this.variantFor(colour, size); if (v) this.setQty(v.id, val); },

      // ---- colour selection / preview ----
      selectColour: function (name) { this.selectedColour = name; this.previewColour = name; this.applyColour(name); },
      setPreviewColour: function (name) { this.previewColour = name; this.applyColour(name); },

      applyColour: function (name) {
        // Find a variant of this colour for its image.
        var v = null;
        for (var i = 0; i < this.variants.length; i++) {
          var cn = this.variants[i].colour || (this.variants[i].options && this.variants[i].options[this.data.colourOptionIndex]);
          if (cn === name) { v = this.variants[i]; break; }
        }
        // Underbase follows selectedColour automatically via the getter, nothing to set.
        // Swap the MAIN gallery image to this colour, respecting current side.
        this.swapMainImage(name, this.viewSide(), v ? v.image : null);
        // Notify the overlay so it re-lays art on the new garment.
        document.dispatchEvent(new CustomEvent("sz:preview-colour", { detail: { colour: name, image: v ? v.image : null } }));
        this.saveState();
      },

      // Which garment face to show in the main image. In "both" mode this
      // follows the active artwork tab; otherwise it follows placement.
      viewSide: function () {
        if (this.placement === "both") return this.activeSide === "back" ? "back" : "front";
        return this.placement === "back" ? "back" : "front";
      },

      // Inner Front/Back artwork tabs (shown in "both" mode) also swap the image.
      setActiveSide: function (side) {
        this.activeSide = side;
        this.swapMainImage(this.previewColour || this.selectedColour, side, null);
      },

      // Swap the main product image to the given colour + side, matched by the
      // color:{name}|view:{side} alt tags added by the image-wiring import.
      swapMainImage: function (name, side, fallbackSrc) {
        var want = String(name || "").toLowerCase();
        var wantView = (side === "back") ? "back" : "front";

        var main = document.querySelector(".splide--product .feature-media-item img") ||
                   document.querySelector(".product-media img");
        if (!main) return;

        // Search ALL gallery imgs but never treat the main display img as a match
        // source (its alt/src get repointed, which would poison the next search).
        var scopes = [".splide--product", ".feature-media-list", ".product-media"];
        var findByAlt = function (view) {
          for (var s = 0; s < scopes.length; s++) {
            var container = document.querySelector(scopes[s]);
            if (!container) continue;
            var imgs = container.querySelectorAll("img");
            for (var i = 0; i < imgs.length; i++) {
              if (imgs[i] === main) continue;
              var alt = (imgs[i].getAttribute("alt") || "").toLowerCase();
              if (alt.indexOf("color:" + want) !== -1 && alt.indexOf("view:" + view) !== -1) return imgs[i];
            }
          }
          return null;
        };

        var target = findByAlt(wantView);
        if (!target && wantView === "back") target = findByAlt("front"); // colour has no back: show its front

        if (target) {
          main.removeAttribute("srcset");
          main.src = target.currentSrc || target.src;
          // Do NOT copy the alt onto main, it would pollute future searches.
        } else if (fallbackSrc) {
          main.removeAttribute("srcset");
          main.src = fallbackSrc;
        }
      },

      colourTotal: function (name) {
        var t = 0, sizes = this.sizesFor(name);
        for (var i = 0; i < sizes.length; i++) t += (this.qty[sizes[i].id] || 0);
        return t;
      },
      sizeTotal: function (size) {
        var t = 0;
        for (var i = 0; i < this.colours.length; i++) { var v = this.variantFor(this.colours[i].name, size); if (v) t += (this.qty[v.id] || 0); }
        return t;
      },
      totalQty: function () {
        var t = 0; for (var k in this.qty) if (this.qty.hasOwnProperty(k)) t += (this.qty[k] || 0); return t;
      },

      filteredColours: function () {
        if (!this.colourSearch) return this.colours;
        var q = this.colourSearch.toLowerCase();
        return this.colours.filter(function (c) { return c.name.toLowerCase().indexOf(q) !== -1; });
      },

      toggleBulk: function () { this.bulkOpen = !this.bulkOpen; },

      // ---- decoration spec setters ----
      // Underbase follows method automatically via the getter, nothing to set here.
      setMethod: function (m) { this.method = m; this.saveState(); },
      setPlacement: function (p) {
        this.placement = p;
        if (p === "back") this.activeSide = "back";
        else this.activeSide = "front";
        // Swap the main garment image to the matching side for the current colour.
        this.swapMainImage(this.previewColour || this.selectedColour, this.viewSide(), null);
        this.saveState();
      },
      uploadSide: function () {
        if (this.isHeadwear) return this.activeZone || (this.placements[0] && this.placements[0].zone) || "";
        return this.placement === "back" ? "back" : this.activeSide;
      },
      // Human-readable label for the current upload target (zone name for headwear).
      uploadSideLabel: function () {
        if (this.isHeadwear) {
          var z = this.zoneById(this.uploadSide());
          return z ? z.name : "placement";
        }
        return this.uploadSide();
      },

      // ---- MOQ ----
      methodMoq: function () {
        if (this.isHeadwear) return this.hwMoq();
        var r = DECO_RATES[this.method]; return r ? r.moq : 1;
      },
      methodLabel: function () { return METHOD_LABELS[this.method] || this.method; },
      moqShortfall: function () { var need = this.methodMoq(), have = this.totalQty(); return have > 0 && have < need ? (need - have) : 0; },

      // ---- price estimate (display only) ----
      tierLabel: function () { var q = this.totalQty(); return q >= 50 ? "50+" : (q >= 10 ? "10-49" : "1-9"); },
      unitPriceFor: function (variant, totalQty) {
        var p10 = variant.price_10_49 != null ? parseFloat(variant.price_10_49) : null;
        var p50 = variant.price_50_plus != null ? parseFloat(variant.price_50_plus) : null;
        var base = parseFloat(variant.basePrice);
        if (totalQty >= 50 && p50 != null) return p50;
        if (totalQty >= 10 && p10 != null) return p10;
        if (totalQty >= 50 && p10 != null) return p10;
        return base;
      },

      // ---- garment tier grid (display, ex GST) ----
      // Picks a representative variant for the selected colour and returns its
      // three garment price tiers. Sizes share the same tier prices, so any
      // in-stock variant of the colour is representative. GARMENT ONLY, ex
      // decoration and setup. See PRICES_INCLUDE_GST for the tax treatment.
      tierRefVariant: function () {
        var name = this.selectedColour || (this.colours[0] && this.colours[0].name);
        var sizes = this.sizesFor(name);
        for (var i = 0; i < sizes.length; i++) {
          var v = this.variantFor(name, sizes[i].size);
          if (v) return v;
        }
        // Fallback: first variant of that colour by scan.
        for (var j = 0; j < this.variants.length; j++) {
          var cn = this.variants[j].colour || (this.variants[j].options && this.variants[j].options[this.data.colourOptionIndex]);
          if (cn === name) return this.variants[j];
        }
        return this.variants[0] || null;
      },

      // Returns [{ label, min, unit, savePct, isBase, active }] for the tier grid.
      tierRows: function () {
        if (this.isHeadwear) return this.hwTierRows();
        var v = this.tierRefVariant();
        if (!v) return [];
        var base = parseFloat(v.basePrice);
        var p10  = v.price_10_49  != null ? parseFloat(v.price_10_49)  : base;
        var p50  = v.price_50_plus != null ? parseFloat(v.price_50_plus) : p10;
        var total = this.totalQty();
        var activeMin = total >= 50 ? 50 : (total >= 10 ? 10 : 1);
        var mk = function (label, min, raw, isBase) {
          var ex = exGst(raw);
          var pct = base > 0 ? Math.round((1 - raw / base) * 100) : 0;
          return { label: label, min: min, unit: ex, savePct: pct, isBase: !!isBase, active: activeMin === min };
        };
        return [
          mk("1-9", 1, base, true),
          mk("10-49", 10, p10, false),
          mk("50+", 50, p50, false)
        ];
      },

      // Per-unit garment price for the CURRENT total qty, ex GST, for the
      // "$x ea." label under each size box.
      unitExGstNow: function () {
        if (this.isHeadwear) return exGst(this.hwBlankUnit());
        var v = this.tierRefVariant();
        if (!v) return 0;
        return exGst(this.unitPriceFor(v, this.totalQty()));
      },

      gstNote: function () { return PRICES_INCLUDE_GST ? "All prices ex GST" : "All prices ex GST"; },
      estTotal: function () {
        if (this.isHeadwear) return r2(exGst(this.hwTotalNow()));
        var total = this.totalQty(); if (!total) return 0;
        var garment = 0;
        for (var k in this.qty) {
          if (!this.qty.hasOwnProperty(k) || !this.qty[k]) continue;
          var v = null;
          for (var i = 0; i < this.variants.length; i++) { if (String(this.variants[i].id) === String(k)) { v = this.variants[i]; break; } }
          if (v) garment += this.unitPriceFor(v, total) * this.qty[k];
        }
        var sides = this.placement === "both" ? 2 : 1;
        var isDtgDtf = (this.method === "dtg" || this.method === "dtf");
        var rateColours = isDtgDtf ? sides : this.colours_count;
        var sidesMult = isDtgDtf ? 1 : sides;
        var rate = impressionRate(this.method, rateColours, total);
        var deco = rate * total * sidesMult;
        var setup = setupFee(this.method, this.colours_count, this.repeat, this.underbase) * sides;
        return r2(garment + deco + setup);
      },
      money: function (n) { return "$" + (Math.round(n * 100) / 100).toFixed(2); },

      // ---- art gate ----
      hasArt: function () {
        if (this.isHeadwear) {
          // Headwear: require artwork for at least the front-style first placement.
          // Art is keyed by placement zone id in this.art for headwear.
          if (!this.placements.length) return false;
          return this.placements.every(function (p) { return !!this.art[p.zone]; }, this);
        }
        if (this.placement === "front") return !!this.art.front;
        if (this.placement === "back")  return !!this.art.back;
        return !!(this.art.front && this.art.back); // both
      },
      canAdd: function () {
        if (this.isHeadwear) {
          return this.totalQty() > 0 && this.placements.length > 0 &&
                 !this.hasPOA() && this.moqShortfall() === 0 && !this.submitting;
          // NOTE: art-per-placement is enforced at submit with a clear message,
          // not in canAdd, so the customer can price before uploading art.
        }
        return this.totalQty() > 0 && this.hasArt() && this.moqShortfall() === 0 && !this.submitting;
      },

      // ---- submit ----
      submit: function () {
        var self = this;
        if (!this.canAdd()) return;
        this.errorMsg = ""; this.submitting = true;

        // Ask the overlay to render a fresh mockup for any side with art, in case
        // the user uploaded and hit Add without dragging. Then send after a short
        // wait so mockupUrl has a chance to populate (not fatal if it doesn't).
        ["front","back"].forEach(function (side) {
          if (self.art[side]) document.dispatchEvent(new CustomEvent("artwork:rendermockup", { detail: { side: side } }));
        });
        setTimeout(function () { self._doSubmit(); }, 900);
      },

      _doSubmit: function () {
        var self = this;
        var items = [];
        for (var k in this.qty) if (this.qty.hasOwnProperty(k) && this.qty[k] > 0) items.push({ variantId: k, qty: this.qty[k] });

        var design;
        if (this.isHeadwear) {
          // Enforce art-per-placement here (canAdd lets them price first).
          var missing = this.placements.filter(function (p) { return !this.art[p.zone]; }, this);
          if (missing.length) {
            this.errorMsg = "Please upload artwork for each placement before adding to cart.";
            this.submitting = false; return;
          }
          var self2 = this;
          design = {
            path: "headwear",
            styleCode: (this.pricing && this.pricing.style_code) || null,
            currency: (this.pricing && this.pricing.currency) || "AUD",
            pricesIncludeGst: !!(this.pricing && this.pricing.prices_include_gst),
            blankUnit: this.hwBlankUnit(),
            setupTotal: this.hwSetupTotal(),
            moq: this.hwMoq(),
            placements: this.placements.map(function (p) {
              var z = self2.zoneById(p.zone) || {};
              var art = self2.art[p.zone] || null;
              return {
                method: p.method,
                zoneName: z.name || "",
                zoneLabel: z.label || p.zone,
                shape: z.shape || "rect",
                maxW_mm: z.w_mm || null, maxH_mm: z.h_mm || null, dia_mm: z.dia_mm || null,
                unitCost: self2.decoUnitFor(p.method),   // always a real rate now
                art: art ? { artUrl: art.url, mockupUrl: art.mockupUrl, position: art.position } : null
              };
            })
          };
        } else {
          design = {
            method: this.method,
            placement: this.placement,
            colours: this.colours_count,
            repeat: this.repeat,
            underbase: this.underbase,
            front: this.art.front ? { artUrl: this.art.front.url, mockupUrl: this.art.front.mockupUrl, position: this.art.front.position } : null,
            back:  this.art.back  ? { artUrl: this.art.back.url,  mockupUrl: this.art.back.mockupUrl,  position: this.art.back.position  } : null
          };
        }

        fetch(this.merchWorker + "/merch-draft-order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ productId: this.productId, items: items, design: design })
        })
        .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, body: j }; }); })
        .then(function (r) {
          if (!r.ok || !r.body.invoice_url) { throw new Error(r.body.error || "Could not create order"); }
          self.clearSaved();
          window.location.href = r.body.invoice_url;
        })
        .catch(function (e) { self.errorMsg = e.message || "Something went wrong"; self.submitting = false; });
      }
    };
  };
})();
